import React, { useContext, useEffect, useState } from "react";
import * as Sentry from '@sentry/browser';
import Api from "../../Api";
import { withAuth } from "../../hoc/Auth";
import { FormattedMessage } from "react-intl";
import MessageContext from "../../MessageContext";
import Search from "../../Components/Utils/SearchTags";

import CrmPayoutBoard from "../../Components/ComponentsOfAdministrator/CrmPayoutBoard";
import CrmPayoutFilters from "../../Components/ComponentsOfAdministrator/CrmPayoutFilters";
import CrmPayoutDetails from "../../Components/ComponentsOfAdministrator/CrmPayoutDetails";
import CrmPayoutActions from "../../Components/ComponentsOfAdministrator/CrmPayoutActions";
import CrmRegistryModal from "../../Components/ComponentsOfAdministrator/CrmRegistryModal";
import CrmPayoutCreateModal from "../../Components/ComponentsOfAdministrator/CrmPayoutCreateModal";

import classes from "./styles.module.css";
import FilterPanel from "../../Components/Utils/FilterPanel";
import Icon from "../../Components/Utils/Icon";
import {
    normalizeListItem,
    STATUS_CONFIG,
    COLUMN_PAGE_SIZE,
    buildRequestFilters,
    statusesToLoad,
    mergeColumnItems,
    flattenColumns,
    columnTotals,
} from "./CrmPayoutRequestsUtils";
import { boardRequestStatuses, columnsForQuickStatus } from "../../Components/ComponentsOfAdministrator/CrmPayoutBoard/CrmPayoutBoardUtils";

var CrmPayoutRequestsPage = withAuth(function (props) {
    var token = props.token;
    var payoutType = props.payoutType || "card";
    var isSbp = payoutType === "sbp";
    var _msg = useContext(MessageContext);
    var onMessage = _msg.onMessage;

    // Data — карточки хранятся по колонкам: { [status]: { items, total, page } }
    var _columns = useState({});
    var columns = _columns[0];
    var setColumns = _columns[1];

    var _loadingStatuses = useState({});
    var loadingStatuses = _loadingStatuses[0];
    var setLoadingStatuses = _loadingStatuses[1];

    var _selected = useState(null);
    var selected = _selected[0];
    var setSelected = _selected[1];

    var _stats = useState(null);
    var stats = _stats[0];
    var setStats = _stats[1];

    var _selectedIds = useState([]);
    var selectedIds = _selectedIds[0];
    var setSelectedIds = _selectedIds[1];

    // Merchants для фильтров
    var _merchants = useState([]);
    var merchants = _merchants[0];
    var setMerchants = _merchants[1];

    // Filters — объект из CrmPayoutFilters
    var _appliedFilters = useState({ filters: {} });
    var appliedFilters = _appliedFilters[0];
    var setAppliedFilters = _appliedFilters[1];

    var _searchTags = useState([]);
    var searchTags = _searchTags[0];
    var setSearchTags = _searchTags[1];

    var _loading = useState(true);
    var loading = _loading[0];
    var setLoading = _loading[1];

    // UI state
    var _filtersOpen = useState(false);
    var filtersOpen = _filtersOpen[0];
    var setFiltersOpen = _filtersOpen[1];

    var _createOpen = useState(false);
    var createOpen = _createOpen[0];
    var setCreateOpen = _createOpen[1];

    // Registries data (для колонки "Реестр" в канбане)
    var _registries = useState([]);
    var registries = _registries[0];
    var setRegistries = _registries[1];

    // Registry modal
    var _registryId = useState(null);
    var registryId = _registryId[0];
    var setRegistryId = _registryId[1];

    // Быстрый фильтр по статусу (клик по бейджу StatsBar)
    var _quickStatus = useState(null);
    var quickStatus = _quickStatus[0];
    var setQuickStatus = _quickStatus[1];

    // Счётчик обновлений для перезагрузки после bulk-действий
    var _refreshKey = useState(0);
    var refreshKey = _refreshKey[0];
    var setRefreshKey = _refreshKey[1];

    // "На сумму" modal state
    var _byAmountOpen = useState(false);
    var byAmountOpen = _byAmountOpen[0];
    var setByAmountOpen = _byAmountOpen[1];

    var _byAmountStep = useState(1);
    var byAmountStep = _byAmountStep[0];
    var setByAmountStep = _byAmountStep[1];

    var _byAmountMerchant = useState("");
    var byAmountMerchant = _byAmountMerchant[0];
    var setByAmountMerchant = _byAmountMerchant[1];

    var _byAmountValue = useState("");
    var byAmountValue = _byAmountValue[0];
    var setByAmountValue = _byAmountValue[1];

    var _byAmountPreview = useState(null);
    var byAmountPreview = _byAmountPreview[0];
    var setByAmountPreview = _byAmountPreview[1];

    var _byAmountName = useState("");
    var byAmountName = _byAmountName[0];
    var setByAmountName = _byAmountName[1];

    var _byAmountLoading = useState(false);
    var byAmountLoading = _byAmountLoading[0];
    var setByAmountLoading = _byAmountLoading[1];

    var _byAmountError = useState("");
    var byAmountError = _byAmountError[0];
    var setByAmountError = _byAmountError[1];

    // Load merchants для фильтров
    useEffect(function () {
        new Api({ token: token }).getProjects().then(function (r) {
            if (Array.isArray(r)) {
                var mapped = r.map(function (p) {
                    return { uuid: p.uuid, displayName: p.displayName || p.name || p.uuid };
                });
                setMerchants(mapped);
            }
        }).catch(function (err) { Sentry.captureException(err); });
    }, [token]);

    // Load stats
    useEffect(function () {
        var projects = appliedFilters.projects || undefined;
        new Api({ token: token }).getCrmPayoutStats(projects, payoutType).then(function (r) {
            setStats(r || null);
        }).catch(function (err) {
            Sentry.captureException(err);
            setStats(null);
        });
    }, [token, refreshKey, appliedFilters, payoutType]);

    // Load registries (для колонки "Реестр" в канбане)
    useEffect(function () {
        var projects = appliedFilters.projects || undefined;
        new Api({ token: token }).getCrmRegistries(projects, payoutType).then(function (r) {
            setRegistries(Array.isArray(r.items) ? r.items : []);
        }).catch(function (err) {
            Sentry.captureException(err);
            setRegistries([]);
        });
    }, [token, refreshKey, appliedFilters, payoutType]);

    // Load board — по запросу на каждую колонку.
    // Раньше грузился ОДИН список на 200 записей и раскладывался по колонкам:
    // как только в одном статусе набиралось 200 свежих заявок, остальные колонки
    // оставались пустыми, хотя счётчики сверху считали их правильно.
    useEffect(function () {
        var cancelled = false;
        var api = new Api({ token: token });
        var baseFilters = buildRequestFilters(appliedFilters, searchTags);
        baseFilters.payoutType = payoutType;
        var projects = (appliedFilters.projects && appliedFilters.projects.length > 0)
            ? appliedFilters.projects
            : null;
        var targetAmount = appliedFilters.filters ? appliedFilters.filters.targetAmount : null;

        setLoading(true);
        setColumns({});

        // Подбор "на сумму" — набор заявок формирует сервер, разбивать по статусам нечего
        if (targetAmount) {
            var payload = {
                page: 1,
                itemsPerPage: 200,
                filters: Object.assign({}, baseFilters, { targetAmount: targetAmount }),
            };
            if (projects) payload.projects = projects;

            api.getCrmPayoutRequests(payload).then(function (r) {
                if (cancelled) return;
                var items = Array.isArray(r.items) ? r.items.map(normalizeListItem) : [];
                var grouped = {};
                items.forEach(function (item) {
                    var s = Number(item.status);
                    if (!grouped[s]) grouped[s] = { items: [], total: 0, page: 1 };
                    grouped[s].items.push(item);
                    grouped[s].total += 1;
                });
                setColumns(grouped);
                setLoading(false);
            }).catch(function (err) {
                Sentry.captureException(err);
                if (cancelled) return;
                setColumns({});
                setLoading(false);
            });

            return function () { cancelled = true; };
        }

        var statuses = statusesToLoad(appliedFilters, boardRequestStatuses());

        if (statuses.length === 0) {
            setLoading(false);
            return function () { cancelled = true; };
        }

        var pending = statuses.length;

        function columnDone() {
            if (cancelled) return;
            pending -= 1;
            if (pending <= 0) setLoading(false);
        }

        statuses.forEach(function (status) {
            var columnPayload = {
                page: 1,
                itemsPerPage: COLUMN_PAGE_SIZE,
                filters: Object.assign({}, baseFilters, { status: [status] }),
            };
            if (projects) columnPayload.projects = projects;

            api.getCrmPayoutRequests(columnPayload).then(function (r) {
                if (cancelled) return;
                var items = Array.isArray(r.items) ? r.items.map(normalizeListItem) : [];
                setColumns(function (prev) {
                    var next = Object.assign({}, prev);
                    next[status] = {
                        items: items,
                        total: typeof r.total === "number" ? r.total : items.length,
                        page: 1,
                    };
                    return next;
                });
                columnDone();
            }).catch(function (err) {
                Sentry.captureException(err);
                if (cancelled) return;
                setColumns(function (prev) {
                    var next = Object.assign({}, prev);
                    next[status] = { items: [], total: 0, page: 1 };
                    return next;
                });
                columnDone();
            });
        });

        return function () { cancelled = true; };
    }, [appliedFilters, searchTags, token, refreshKey, payoutType]);

    // Подгрузка следующей страницы конкретной колонки
    function handleLoadMore(status) {
        var col = columns[status];
        if (!col || loadingStatuses[status]) return;
        if (col.items.length >= col.total) return;

        var nextPage = (col.page || 1) + 1;
        var payload = {
            page: nextPage,
            itemsPerPage: COLUMN_PAGE_SIZE,
            filters: Object.assign({}, buildRequestFilters(appliedFilters, searchTags), {
                status: [Number(status)], payoutType: payoutType
            }),
        };
        if (appliedFilters.projects && appliedFilters.projects.length > 0) {
            payload.projects = appliedFilters.projects;
        }

        setLoadingStatuses(function (prev) {
            var next = Object.assign({}, prev);
            next[status] = true;
            return next;
        });

        new Api({ token: token }).getCrmPayoutRequests(payload).then(function (r) {
            var items = Array.isArray(r.items) ? r.items.map(normalizeListItem) : [];
            setColumns(function (prev) {
                var next = Object.assign({}, prev);
                var current = prev[status] || { items: [], total: 0, page: 1 };
                next[status] = {
                    items: mergeColumnItems(current.items, items),
                    total: typeof r.total === "number" ? r.total : current.total,
                    page: nextPage,
                };
                return next;
            });
        }).catch(function (err) {
            Sentry.captureException(err);
            onMessage({ type: "error", message: "Не удалось загрузить ещё заявки" });
        }).then(function () {
            setLoadingStatuses(function (prev) {
                var next = Object.assign({}, prev);
                delete next[status];
                return next;
            });
        });
    }

    var list = flattenColumns(columns);
    var totals = columnTotals(columns);

    function onChangeSearch(tags) {
        setSearchTags(tags);
    }

    function onSelectRequest(row) {
        if (row && row.id) {
            new Api({ token: token }).getCrmPayoutRequest(row.id).then(function (full) {
                setSelected(full || row);
            }).catch(function (err) {
                Sentry.captureException(err);
                setSelected(row);
            });
        } else {
            setSelected(null);
        }
    }

    function onRequestUpdate(updatedRequest) {
        setSelected(updatedRequest);
        setRefreshKey(function (k) { return k + 1; });
    }

    function onApplyFilters(newFilters) {
        setAppliedFilters(newFilters || { filters: {} });
        setQuickStatus(null);
    }

    // Checkbox handlers
    function handleCheck(id, checked) {
        if (checked) {
            setSelectedIds(function (prev) { return prev.concat([id]); });
        } else {
            setSelectedIds(function (prev) { return prev.filter(function (x) { return x !== id; }); });
        }
    }

    function handleBulkComplete() {
        setSelectedIds([]);
        setSelected(null);
        setRefreshKey(function (k) { return k + 1; });
    }

    // "Выбрать все" в колонке канбана
    function handleCheckAllInColumn(status, checked, columnItemIds) {
        if (checked) {
            setSelectedIds(function (prev) {
                var newIds = prev.slice();
                columnItemIds.forEach(function (id) {
                    if (newIds.indexOf(id) === -1) {
                        newIds.push(id);
                    }
                });
                return newIds;
            });
        } else {
            setSelectedIds(function (prev) {
                return prev.filter(function (id) {
                    return columnItemIds.indexOf(id) === -1;
                });
            });
        }
    }

    // Быстрый фильтр по статусу — клик по бейджу
    function handleQuickStatusFilter(statusValue) {
        if (quickStatus === statusValue) {
            setQuickStatus(null);
        } else {
            setQuickStatus(statusValue);
        }
    }

    // Registry modal
    function handleRegistryClick(regId) {
        setRegistryId(regId);
    }

    // Drag & Drop — перемещение карточки между колонками
    function handleDragDrop(requestId, fromStatus, toStatus) {
        var api = new Api({ token: token });

        // Отклонение (→ status 2)
        if (toStatus === 2) {
            api.declineCrmPayoutRequest(requestId, "Перемещено вручную").then(function () {
                setRefreshKey(function (k) { return k + 1; });
            }).catch(function (e) {
                Sentry.captureException(e);
                var msg = (e.response && e.response.data && e.response.data.message) || e.message || "Ошибка";
                onMessage({ type: "error", message: "Ошибка перемещения: " + msg });
            });
            return;
        }

        // Отправить на антифрод (Создана → Ожидает, status 0 → 7)
        if (fromStatus === 0 && toStatus === 7) {
            api.sendToAntifraudCrmPayoutRequests([requestId]).then(function () {
                setRefreshKey(function (k) { return k + 1; });
            }).catch(function (e) {
                Sentry.captureException(e);
                var msg = (e.response && e.response.data && e.response.data.message) || e.message || "Ошибка";
                onMessage({ type: "error", message: "Ошибка отправки на антифрод: " + msg });
            });
            return;
        }

        // Вернуть в ожидание (→ status 7, из реестра или ошибки)
        if (toStatus === 7) {
            api.moveBackToAwaitingCrmPayoutRequests([requestId]).then(function () {
                setRefreshKey(function (k) { return k + 1; });
            }).catch(function (e) {
                Sentry.captureException(e);
                var msg = (e.response && e.response.data && e.response.data.message) || e.message || "Ошибка";
                onMessage({ type: "error", message: "Ошибка перемещения: " + msg });
            });
            return;
        }

        // Перевести в успех (→ status 5)
        if (toStatus === 5) {
            api.moveToSuccessCrmPayoutRequest(requestId, null).then(function () {
                setRefreshKey(function (k) { return k + 1; });
            }).catch(function (e) {
                Sentry.captureException(e);
                var msg = (e.response && e.response.data && e.response.data.message) || e.message || "Ошибка";
                onMessage({ type: "error", message: "Ошибка перемещения: " + msg });
            });
            return;
        }
    }

    // "На сумму" handlers
    function openByAmountModal() {
        setByAmountOpen(true);
        setByAmountStep(1);
        setByAmountMerchant("");
        setByAmountValue("");
        setByAmountPreview(null);
        setByAmountName("");
        setByAmountError("");
    }

    function closeByAmountModal() {
        setByAmountOpen(false);
    }

    function handleByAmountPreview() {
        if (!byAmountMerchant || !byAmountValue || Number(byAmountValue) <= 0) {
            setByAmountError("Выберите мерчанта и укажите сумму");
            return;
        }
        setByAmountLoading(true);
        setByAmountError("");
        new Api({ token: token }).formCrmRegistryByAmount(byAmountMerchant, Number(byAmountValue), null, payoutType).then(function (r) {
            setByAmountPreview(r);
            setByAmountStep(2);
            setByAmountLoading(false);
        }).catch(function (e) {
            Sentry.captureException(e);
            var msg = (e.response && e.response.data && e.response.data.message) || e.message || "Ошибка";
            setByAmountError(msg);
            setByAmountLoading(false);
        });
    }

    function handleByAmountForm() {
        if (!byAmountName.trim()) {
            setByAmountError("Введите название реестра");
            return;
        }
        setByAmountLoading(true);
        setByAmountError("");
        new Api({ token: token }).formCrmRegistryByAmount(byAmountMerchant, Number(byAmountValue), byAmountName.trim(), payoutType).then(function (r) {
            setByAmountLoading(false);
            setByAmountOpen(false);
            setRefreshKey(function (k) { return k + 1; });
            onMessage({ type: "success", message: "Реестр \"" + byAmountName.trim() + "\" сформирован (" + r.count + " заявок на " + r.totalAmount.toLocaleString("ru-RU", { minimumFractionDigits: 2 }) + " \u20BD)" });
        }).catch(function (e) {
            Sentry.captureException(e);
            var msg = (e.response && e.response.data && e.response.data.message) || e.message || "Ошибка";
            setByAmountError(msg);
            setByAmountLoading(false);
        });
    }

    // Подсчёт сводки из статистики
    var totalAmount = 0;
    var totalRequests = 0;
    var statsMap = {};
    if (stats && stats.stats) {
        stats.stats.forEach(function (s) {
            totalRequests += s.count;
            totalAmount += s.totalAmount;
            statsMap[s.status] = s;
        });
    }

    // Определяем, какие статусы показывать в канбане
    var visibleStatuses = columnsForQuickStatus(quickStatus);

    var pageContent = (
        <div className={classes.PageContainer + " d-flex flex-column min-vh-100"}>
            <div className="row flex-grow-1">
                <div className="col-12 pt-2">
                    {/* Header */}
                    <div className={classes.PageHeader}>
                        <h4 className={classes.PageTitle}>
                            <FormattedMessage
                                id={isSbp ? "CrmSbpPayout.title" : "CrmPayout.title"}
                                defaultMessage={isSbp ? "CRM-заявки СБП" : "CRM-заявки на выплаты"}
                            />
                        </h4>
                        <div className={classes.HeaderButtons}>
                            <button
                                className="btn btn-sm btn-success"
                                onClick={function () { setCreateOpen(true); }}
                            >
                                Создать заявку
                            </button>
                            <button
                                className={"btn btn-sm " + (filtersOpen ? "btn-primary" : "btn-outline-primary")}
                                onClick={function () { setFiltersOpen(!filtersOpen); }}
                            >
                                <FormattedMessage id="CrmPayout.filters.title" defaultMessage="\u0424\u0438\u043B\u044C\u0442\u0440\u044B" />
                            </button>
                            <button
                                className="btn btn-sm btn-outline-success"
                                onClick={openByAmountModal}
                            >
                                На сумму
                            </button>
                        </div>
                    </div>

                    {/* Search — расширенный */}
                    <Search
                        onChangeSearch={onChangeSearch}
                        isDisabled={false}
                        defaultSearch={{
                            key: "fullName",
                            locale: "CrmPayout.search.fullName",
                            placeholder: "CrmPayout.search.fullName.placeholder"
                        }}
                        searchBy={[
                            {
                                key: "fullName",
                                locale: "CrmPayout.search.fullName",
                                placeholder: "CrmPayout.search.fullName.placeholder"
                            },
                            {
                                key: "phone",
                                locale: "CrmPayout.search.phone",
                                placeholder: "CrmPayout.search.phone.placeholder"
                            },
                            !isSbp && {
                                key: "cardMask",
                                locale: "CrmPayout.search.cardMask",
                                placeholder: "CrmPayout.search.cardMask.placeholder"
                            },
                            {
                                key: "merchantName",
                                locale: "CrmPayout.search.merchantName",
                                placeholder: "CrmPayout.search.merchantName.placeholder"
                            },
                            {
                                key: "transactionId",
                                locale: "CrmPayout.search.transactionId",
                                placeholder: "CrmPayout.search.transactionId.placeholder"
                            },
                            {
                                key: "externalReference",
                                locale: "CrmPayout.search.externalReference",
                                placeholder: "CrmPayout.search.externalReference.placeholder"
                            },
                        ].filter(Boolean)}
                    />

                    {/* Stats Bar — бейджи по статусам */}
                    <div className={classes.StatsBar}>
                        {STATUS_CONFIG.map(function (sc) {
                            var s = statsMap[sc.status];
                            var count = s ? s.count : 0;
                            var isActive = quickStatus === sc.status;
                            var badgeStyle = isActive
                                ? { background: sc.color, color: "#fff", borderColor: sc.color }
                                : { background: sc.bg, color: sc.color, borderColor: "transparent" };

                            return (
                                <button
                                    key={sc.status}
                                    className={classes.StatsBadge}
                                    style={badgeStyle}
                                    onClick={function () { handleQuickStatusFilter(sc.status); }}
                                    type="button"
                                    title={sc.label}
                                >
                                    <span className={classes.StatsBadgeLabel}>{sc.label}</span>
                                    <span className={classes.StatsBadgeCount}>{count}</span>
                                </button>
                            );
                        })}
                        <div className={classes.StatsTotalBadge}>
                            <span className={classes.StatsBadgeLabel}>
                                <FormattedMessage id="CrmPayout.stats.total" defaultMessage="\u0412\u0441\u0435\u0433\u043E" />
                            </span>
                            <span className={classes.StatsBadgeCount}>{totalRequests}</span>
                            {totalAmount > 0 && (
                                <span className={classes.StatsTotalAmount}>
                                    {totalAmount.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {"\u20BD"}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Bulk actions bar — контекстные */}
                    <CrmPayoutActions
                        selectedIds={selectedIds}
                        token={token}
                        data={list}
                        registries={registries}
                        onComplete={handleBulkComplete}
                        onClearSelection={function () { setSelectedIds([]); }}
                        onMessage={onMessage}
                    />

                    {/* Kanban Board */}
                    <CrmPayoutBoard
                        isLoading={loading}
                        data={list}
                        registries={registries}
                        currentId={selected ? selected.id : null}
                        selectedIds={selectedIds}
                        visibleStatuses={visibleStatuses}
                        totals={totals}
                        loadingStatuses={loadingStatuses}
                        onLoadMore={handleLoadMore}
                        onSelect={onSelectRequest}
                        onCheck={handleCheck}
                        onCheckAllInColumn={handleCheckAllInColumn}
                        onRegistryClick={handleRegistryClick}
                        onDragDrop={handleDragDrop}
                    />

                    {/* Detail modal */}
                    {selected && (
                        <CrmPayoutDetails
                            request={selected}
                            token={token}
                            onUpdate={onRequestUpdate}
                            onClose={function () { setSelected(null); }}
                            onRegistryClick={handleRegistryClick}
                            onMessage={onMessage}
                        />
                    )}

                    {/* Registry modal */}
                    {registryId && (
                        <CrmRegistryModal
                            registryId={registryId}
                            payoutType={payoutType}
                            registryInfo={registries.find(function (r) { return r.id === registryId; }) || null}
                            token={token}
                            onClose={function () { setRegistryId(null); }}
                            onComplete={function () { setRefreshKey(function (k) { return k + 1; }); }}
                            onMessage={onMessage}
                        />
                    )}

                    {createOpen && (
                        <CrmPayoutCreateModal
                            token={token}
                            payoutType={payoutType}
                            merchants={merchants}
                            onClose={function () { setCreateOpen(false); }}
                            onCreated={function () {
                                setCreateOpen(false);
                                setRefreshKey(function (k) { return k + 1; });
                            }}
                            onMessage={onMessage}
                        />
                    )}

                    {/* "На сумму" modal */}
                    {byAmountOpen && (
                        <div className={classes.ModalBackdrop} onClick={closeByAmountModal}>
                            <div className={classes.Modal} onClick={function (e) { e.stopPropagation(); }}>
                                <div className={classes.ModalHeader}>
                                    <h5 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Сформировать реестр на сумму</h5>
                                    <button type="button" className={classes.ModalClose} onClick={closeByAmountModal}>
                                        <Icon icon="close" size="15px" />
                                    </button>
                                </div>
                                <div className={classes.ModalBody}>
                                    {byAmountError && (
                                        <div className="alert alert-danger py-1 mb-2">{byAmountError}</div>
                                    )}
                                    {byAmountStep === 1 && (
                                        <div>
                                            <div className="form-group mb-2">
                                                <label>Мерчант</label>
                                                <select
                                                    className="form-control"
                                                    value={byAmountMerchant}
                                                    onChange={function (e) { setByAmountMerchant(e.target.value); }}
                                                >
                                                    <option value="">-- Выберите --</option>
                                                    {merchants.map(function (m) {
                                                        return <option key={m.uuid} value={m.uuid}>{m.displayName}</option>;
                                                    })}
                                                </select>
                                            </div>
                                            <div className="form-group mb-2">
                                                <label>Целевая сумма (руб.)</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={byAmountValue}
                                                    onChange={function (e) { setByAmountValue(e.target.value); }}
                                                    min="1"
                                                    step="0.01"
                                                    placeholder="Например: 2000000"
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {byAmountStep === 2 && byAmountPreview && (
                                        <div>
                                            {byAmountPreview.count === 0 ? (
                                                <div className="alert alert-warning py-2">
                                                    Нет подходящих заявок для указанной суммы
                                                </div>
                                            ) : (
                                                <div>
                                                    <div className="alert alert-info py-2 mb-2">
                                                        <strong>{byAmountPreview.merchantName}</strong><br />
                                                        Будет выбрано: <strong>{byAmountPreview.count}</strong> заявок<br />
                                                        На сумму: <strong>{byAmountPreview.totalAmount.toLocaleString("ru-RU", { minimumFractionDigits: 2 })} {"\u20BD"}</strong>
                                                    </div>
                                                    <div className="form-group mb-2">
                                                        <label>Название реестра</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={byAmountName}
                                                            onChange={function (e) { setByAmountName(e.target.value); }}
                                                            placeholder="Название реестра"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className={classes.ModalFooter}>
                                    <button className="btn btn-secondary" onClick={closeByAmountModal}>Отмена</button>
                                    {byAmountStep === 1 && (
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleByAmountPreview}
                                            disabled={byAmountLoading}
                                        >
                                            {byAmountLoading ? "Загрузка..." : "Подобрать"}
                                        </button>
                                    )}
                                    {byAmountStep === 2 && byAmountPreview && byAmountPreview.count > 0 && (
                                        <div style={{ display: "flex", gap: 8 }}>
                                            <button
                                                className="btn btn-outline-primary"
                                                onClick={function () { setByAmountStep(1); setByAmountPreview(null); setByAmountError(""); }}
                                            >
                                                Назад
                                            </button>
                                            <button
                                                className="btn btn-success"
                                                onClick={handleByAmountForm}
                                                disabled={byAmountLoading}
                                            >
                                                {byAmountLoading ? "Формирование..." : "Сформировать"}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <FilterPanel title="Фильтры" isOpen={filtersOpen} onClose={function () { setFiltersOpen(false); }} content={pageContent}>
            <CrmPayoutFilters
                payoutType={payoutType}
                isOpen={filtersOpen}
                onClose={function () { setFiltersOpen(false); }}
                onApplyFilters={onApplyFilters}
                merchants={merchants}
                isLoading={loading}
            />
        </FilterPanel>
    );
});

export default CrmPayoutRequestsPage;



// WEBPACK FOOTER //
// ./src/PagesOfAdministrator/CrmPayoutRequests/index.js